import zionDebug from '@fs/zion-debug'
import { NoticeLoading } from '@fs/zion-icon'
import { ComboViewer, RibbonViewer, StaticImageViewer } from '@fs/zion-image-viewer'
import { useQueryParams } from '@fs/zion-router'
import { useSlsHighlightsContext, useSlsStateContext } from '@fs/zion-sls'
import { Row, useOverlay, useZoomPan } from '@fs/zion-ui'
import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import useInitialMaintainZoomPan from '../../../shared/hooks/useInitialMaintainZoom'
import useStatusOverlayTrigger from '../../hooks/useStatusOverlayTrigger'
import DownloadOverlay from '../../views/fullText/components/downloadOverlay/DownloadOverlay'
import useDownloadOverlayData from '../../views/fullText/hooks/useDownloadOverlayData'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import { useR2DispatchContext, useR2Highlights, useR2StateContext } from '../../providers/r2Provider/R2Context'
import SlsHighlightsBase from '../slsHighlightsBase/SlsHighlightsBase'
import SplitScreenContainer from '../splitScreenContainer/SplitScreenContainer'
import useTrackCollectionView from '../../hooks/useTrackCollectionView'
import { useCollectionContextProvider } from '../../providers/collectionProvider/CollectionProvider'
import {
  useSharedBrowserDispatchContext,
  useSharedBrowserStateContext,
} from '../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import Controls from './Controls'
import { useDarkModeContext } from '../../../shared/providers/darkModeProvider/DarkModeProvider'
import useAllowDarkMode from '../../../shared/hooks/useAllowDarkMode'
import {
  useImageBrowserSetup,
  useImageHighlightInitialization,
  useR2PanelCloser,
  useRectangleDrawnHandlerSlsHook,
  useSections,
  useSetFocusedIndex,
  useSetGridParamInURL,
  useSetReferenceFocusedIndex,
  useSetSplitScreenImageGrid,
  useColumnCount,
  useInitializeViewModeFromURL,
} from './hooks'
import DarkModeControl from '../../../shared/components/darkModeControl/DarkModeControl'
import HighlightsControl from '../../../shared/components/highlightsControl/HighlightsControl'
import HighlightsVisibilityProvider from '../../../shared/providers/highlightsVisibilityProvider/HighlightsVisibilityProvider'
import { getPanMarginProps } from '../../utils/zoomPanUtils'

const debug = zionDebug('siv:SharedImageBrowserViewer')

const viewerContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  minWidth: 0,
}

export default function R2Image() {
  const [t] = useTranslation()
  const { theme, resetDarkMode } = useDarkModeContext()
  const { query } = useQueryParams({ replaceState: true })
  const [imageViewer] = useZoomPan()
  const [referenceViewer] = useZoomPan()
  const { view = 'explore' } = query
  const { isCreatingRects, isInEditMode, init } = useSlsHighlightsContext()
  const { openOverlay, closeOverlay } = useOverlayContext()
  const { triggerStatusOverlay } = useStatusOverlayTrigger()
  const downloadOverlay = useOverlay()
  const { displayHighlights } = useR2Highlights()
  const r2Dispatch = useR2DispatchContext()
  const sharedBrowserState = useSharedBrowserStateContext()
  const sharedBrowserDispatch = useSharedBrowserDispatchContext()
  const { collectionId } = useCollectionContextProvider()
  const { viewMode, thumbnails, viewedImages } = sharedBrowserState
  const r2State = useR2StateContext()
  const zionSlsState = useSlsStateContext()
  const { currentImageArk, images: imagesData } = zionSlsState
  const { citation, plainTextTranscript } = useDownloadOverlayData()
  const initialMaintainZoomPanProps = useInitialMaintainZoomPan()
  const [columnCount, setColumnCount] = useColumnCount(r2State.splitScreen)
  const [referenceColumnCount, setReferenceColumnCount] = useColumnCount(r2State.splitScreen)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Flag images the patron has already focused so the image viewer renders the "viewed" badge.
  // Covers both the grid (viewedThumbnails) and the ribbon (ribbonImages, derived below).
  const viewedThumbnails = useMemo(
    () => thumbnails?.map((x) => ({ ...x, viewed: viewedImages.has(x.ark) })),
    [thumbnails, viewedImages]
  )

  const ribbonImages = viewedThumbnails?.map((x) => ({
    alt: x.alt,
    id: x.id,
    src: x.srcSet?.[0] || '',
    viewed: x.viewed,
  }))

  useTrackCollectionView(collectionId, currentImageArk)

  /**
   * A Warning:
   * Do not be fooled by the seeming lack of arguments. The following hooks make heavy usage of context
   */
  const sections = useSections()
  // IMPORTANT: Call useInitializeViewModeFromURL BEFORE useImageBrowserSetup to avoid race conditions
  useInitializeViewModeFromURL()
  useImageBrowserSetup()
  useImageHighlightInitialization()
  useR2PanelCloser()
  useAllowDarkMode()

  const setSplitScreenImageGrid = useSetSplitScreenImageGrid()
  const setReferenceFocusedIndex = useSetReferenceFocusedIndex()
  const rectangleDrawnHandler = useRectangleDrawnHandlerSlsHook()
  const setGridParamInURL = useSetGridParamInURL()
  const setFocusedIndex = useSetFocusedIndex()

  const handleAdjustmentControlReset = useCallback(() => {
    resetDarkMode()
  }, [resetDarkMode])

  const loading = useMemo(
    () => sharedBrowserState.focusedIndex === -1 || !thumbnails.length,
    [thumbnails, sharedBrowserState.focusedIndex]
  )

  debug({
    currentImageArk,
    focusedIndex: sharedBrowserState.focusedIndex,
    imagesData,
    ribbonImages,
    thumbnails,
    loading,
    showImageGrid: query?.grid === 'on',
    r2State,
    sections,
  })

  // Commented out for now because of jira LCOM-1258
  // useKeyboardShortcuts({
  //   handlers: {
  //     KeyI: {
  //       action: () => infoSheetOverlay?.handleClick(),
  //       title: t('toggle-infosheet.button', 'Toggle Panel'),
  //     },
  //   },
  //   enabled: true,
  // })

  // Loading highlights for the image in the new highlights hook
  useEffect(() => {
    const referenceImageArk =
      r2State.splitScreen && !r2State.splitScreenImageGrid
        ? thumbnails && thumbnails?.[r2State.referenceFocusedIndex]?.ark
        : undefined

    init({ mainImageArk: currentImageArk, splitScreenImageArk: referenceImageArk })
  }, [
    currentImageArk,
    init,
    r2State.referenceFocusedIndex,
    r2State.splitScreen,
    r2State.splitScreenImageGrid,
    thumbnails,
  ])

  const panMarginProps = getPanMarginProps()

  return (
    <HighlightsVisibilityProvider>
      {loading ? (
        <Row alignX="center" alignY="middle">
          <NoticeLoading size="md" />
        </Row>
      ) : (
        <>
          <SplitScreenContainer
            appName="Rii"
            splitScreenIsOpen={r2State.splitScreen}
            setSplitScreenIsOpen={(value) => r2Dispatch({ type: 'SET_SPLIT_SCREEN', value })}
            splitScreenTitleText={t('shared-image-viewer.view-only', 'View Only: Image {index}', {
              index: r2State.referenceFocusedIndex + 1,
            })}
            splitScreenRibbonReady={!r2State.splitScreenImageGrid && ribbonImages.length > 0}
            mainRibbonReady={query?.grid !== 'on' && ribbonImages.length > 0}
            mainImageTitleText={t('shared-image-viewer.original', 'Original: Image {index}', {
              index: sharedBrowserState.focusedIndex + 1,
            })}
            SplitScreenImageViewer={
              <section
                data-theme={theme}
                aria-label={t('film-viewer.accessibility-heading', 'Image Viewer')}
                style={viewerContainerStyle}
              >
                <ComboViewer
                  {...referenceViewer}
                  testingKey="r2-split-screen"
                  images={viewedThumbnails}
                  setFocusedIndex={setReferenceFocusedIndex}
                  focusedIndex={r2State.referenceFocusedIndex}
                  ImageComponent={StaticImageViewer}
                  showImageGrid={r2State.splitScreenImageGrid}
                  setShowImageGrid={setSplitScreenImageGrid}
                  setColumnCount={setReferenceColumnCount}
                  columnCount={referenceColumnCount}
                  adjustmentControls={
                    <>
                      <DarkModeControl />
                      <HighlightsControl />
                    </>
                  }
                  onAdjustmentControlReset={handleAdjustmentControlReset}
                  downloadControl
                  numbered
                  {...initialMaintainZoomPanProps}
                  minZoom={0.8}
                  {...panMarginProps}
                >
                  {(view === 'fullText' || view === 'index') && (
                    <SlsHighlightsBase
                      showEditHighlights={isInEditMode}
                      forSplitScreen
                      state={r2State}
                      dispatch={r2Dispatch}
                    />
                  )}
                </ComboViewer>
              </section>
            }
            SplitScreenRibbonViewer={
              <RibbonViewer
                focusedIndex={r2State.referenceFocusedIndex}
                setFocusedIndex={setReferenceFocusedIndex}
                images={ribbonImages || []}
              />
            }
            MainImageViewer={
              <section
                data-theme={theme}
                aria-label={t('film-viewer.accessibility-heading', 'Image Viewer')}
                style={viewerContainerStyle}
              >
                <ComboViewer
                  {...imageViewer}
                  testingKey="r2-main-image"
                  drawingMode={isCreatingRects || r2State.creatingRect}
                  onRectangleDrawn={rectangleDrawnHandler}
                  prompt={
                    isCreatingRects || r2State.creatingRect
                      ? t('draw-highlight.body', 'Click anywhere and drag to create a highlight')
                      : ''
                  }
                  setFocusedIndex={setFocusedIndex}
                  focusedIndex={thumbnails.length ? sharedBrowserState.focusedIndex : 0}
                  showImageGrid={query?.grid === 'on'}
                  setShowImageGrid={setGridParamInURL}
                  setColumnCount={setColumnCount}
                  columnCount={columnCount}
                  images={viewedThumbnails}
                  ImageComponent={StaticImageViewer}
                  adjustmentControls={
                    <>
                      <DarkModeControl />
                      <HighlightsControl />
                    </>
                  }
                  onAdjustmentControlReset={handleAdjustmentControlReset}
                  downloadControl
                  onDownload={async () => {
                    try {
                      downloadOverlay.handleClick()
                    } catch (e) {
                      triggerStatusOverlay()
                    }
                  }}
                  sections={sections}
                  controls={<Controls {...{ viewMode }} />}
                  gridControls={<Controls {...{ viewMode }} />}
                  numbered
                  {...initialMaintainZoomPanProps}
                  {...panMarginProps}
                  minZoom={0.8}
                  onLoad={() => {
                    sharedBrowserDispatch({ type: 'SET_IMAGE_LOADED', value: true })
                    // Only auto-open the infoSheet on initial page load, not when navigating between images
                    if (isInitialLoad) {
                      openOverlay('mainInfosheet')
                      setIsInitialLoad(false)
                    }
                  }}
                  onError={(err) => {
                    sharedBrowserDispatch({ type: 'SET_IMAGE_ERROR', value: err })
                    const imageStatusRestricted = err.status !== 'OK' && err.status !== 'UNAVAILABLE'
                    if (imageStatusRestricted) {
                      closeOverlay('mainInfosheet')
                    } else if (isInitialLoad) {
                      // Only auto-open on initial load
                      openOverlay('mainInfosheet')
                      setIsInitialLoad(false)
                    }
                  }}
                >
                  {(view === 'index' || view === 'fullText') && (
                    <SlsHighlightsBase showEditHighlights={isInEditMode} state={r2State} dispatch={r2Dispatch} />
                  )}
                </ComboViewer>
              </section>
            }
            MainRibbonViewer={
              <RibbonViewer
                focusedIndex={sharedBrowserState.focusedIndex > -1 ? sharedBrowserState.focusedIndex : 0}
                setFocusedIndex={setFocusedIndex}
                images={ribbonImages || []}
              />
            }
          />

          <DownloadOverlay
            downloadOverlay={downloadOverlay}
            displayHighlights={displayHighlights}
            triggerStatusOverlay={triggerStatusOverlay}
            citation={citation}
            plainTextTranscript={plainTextTranscript}
            query={query}
          />
        </>
      )}
    </HighlightsVisibilityProvider>
  )
}
